import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import { renderNarrative, hasFigToken } from "../metrics/renderNarrative";

const DEFAULT_EMPTY = "No summary available for this period.";

/**
 * PageSummaryBlock - per-page narrative summary (page_summary block type).
 *
 * Renders from an injected `narrative` prop (run.narrative) - not fetched, mirroring
 * ExecNarrativeBlock/NotesBlock. models.BoardReportRun.Narrative is a free-form
 * map[string]interface{} on the backend, serialized snake_case (boardreport.Narrative,
 * Task 7): overview_summary/performance_summary/drivers_summary/forecast_summary are each
 * a deterministic per-page prose summary. `binding.summaryKey` selects which one this page
 * shows (e.g. "performance_summary" on the Performance page) - the same block type is reused
 * across pages with a different binding, rather than one hardcoded component per page.
 *
 * SP6: the summary text may contain `{{fig:KEY}}` @figure tokens - rendered live via
 * renderNarrative from the optional `metrics` prop (ManifestPage/BoardReportPages forward it;
 * absent -> tokens render "—", a token-free summary is unaffected). When the summary contains a
 * token, `BaseBlock`'s `isLoading` is gated on `metricsLoading` so the PDF poller waits for
 * metrics to resolve before capturing; a token-free summary keeps `isLoading={false}`.
 */
const PageSummaryBlock = ({
  title = "Summary",
  narrative,
  binding = {},
  metrics,
  metricsLoading = false,
}) => {
  const { summaryKey } = binding;
  const summary = summaryKey ? narrative?.[summaryKey] : undefined;
  const isLoading = hasFigToken(summary) && metricsLoading;

  return (
    <BaseBlock title={title} isLoading={isLoading} error={null} className="a4-page-summary">
      {summary ? (
        <p className="a4-page-summary-text">{renderNarrative(summary, metrics)}</p>
      ) : (
        <p className="a4-page-summary-empty">{DEFAULT_EMPTY}</p>
      )}
    </BaseBlock>
  );
};

PageSummaryBlock.propTypes = {
  title: PropTypes.string,
  narrative: PropTypes.object,
  binding: PropTypes.shape({
    summaryKey: PropTypes.string,
  }),
  metrics: PropTypes.object,
  metricsLoading: PropTypes.bool,
};

export default PageSummaryBlock;
